import { createTheme, Box } from "@mui/material"
import { styled, Theme, CSSObject } from "@mui/material/styles"
import MuiDrawer from "@mui/material/Drawer"
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar"
import { colors } from "../../../styles/commonStyles"

export const drawerWidth = 246

export const customTheme = createTheme({
  typography: {
    fontFamily: ["Poppins"].join(","),
  },
  palette: {
    primary: {
      main: "#0052cc",
    },
    secondary: {
      main: "#edf2ff",
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: "#ffffff",
          color: "#454545",
          boxShadow: "0 4px 2px -1px  #0000000F",
          height: "10vh",
          justifyContent: "center",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          width: "256px",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {},
    },
  },
})

export const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
})

export const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

export interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  // padding: "0px",
  // height: "100%",
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}))

export const DrawerHeader = styled(Box)(({ theme }) => ({
  height: "10vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  // padding: "0",
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

export const ProntoLogoIcon = () => (
  <svg
    id="Group_12446"
    data-name="Group 12446"
    xmlns="http://www.w3.org/2000/svg"
    width="43.191"
    height="40.458"
    viewBox="0 0 43.191 40.458"
  >
    <path
      id="Path_11"
      data-name="Path 11"
      d="M337.911,72.942c0,.627-.032,1.255-.093,1.876a26.135,26.135,0,0,1-3.927,10.953,44.982,44.982,0,0,1-7.56,9.148c-.906.852-3.742,3.166-3.742,3.166s-5.316-4.657-6.7-6.2a46.976,46.976,0,0,1-3.111-3.86,35.551,35.551,0,0,1-2.5-4.014c-.333-.621-.643-1.255-.928-1.895-.1-.221-.192-.445-.285-.669-.086-.208-.17-.416-.25-.628-.157-.41-.307-.829-.442-1.248-.022-.07-.045-.138-.067-.208a18.725,18.725,0,0,1-.583-10.147,15.325,15.325,0,0,1,13.827-11.558c.342-.025.688-.035,1.03-.035a15.448,15.448,0,0,1,9.891,3.62,15.18,15.18,0,0,1,2.154,2.244,15.419,15.419,0,0,1,2.788,5.62c.1.371.182.749.25,1.127a15.211,15.211,0,0,1,.2,1.571c0,.058.01.112.01.17C337.9,72.3,337.911,72.622,337.911,72.942Z"
      transform="translate(-304.743 -57.626)"
      fill="#42b2fc"
    />
    <ellipse
      id="Ellipse_1"
      data-name="Ellipse 1"
      cx="12.637"
      cy="12.637"
      rx="12.637"
      ry="12.637"
      transform="translate(2.525 24.782) rotate(-76.947)"
      fill="#fff"
    />
    <path
      id="Path_12"
      data-name="Path 12"
      d="M336.1,78.046a4.24,4.24,0,0,0,3.2.935,5.19,5.19,0,0,0,2.822-1.284s-2.678.67-3.518-.125a13.732,13.732,0,0,0,2.786-.861c1.368-.608,3.058-1.009,4.172.713a8.16,8.16,0,0,1,.306,7.709c-.845,1.5-.954,1.207-.545,3.3a7.493,7.493,0,0,1-.738,5.907c-.731,1.332-2.729,2.46-3.357,1.911-.5-.438,1.185-1.14.693-4.316-.475-3.069-1.925-3.244-3.172-3.182-1.313.066-2.552,1.229-2.552,3.358,0,2.6.806,3.2.7,4.007s-2.43-.288-3.747-2.263a8.436,8.436,0,0,1-.747-6.713c.608-1.621-.793-2.468-1.462-5a5.411,5.411,0,0,1,1.616-5.075C334.058,76.089,335.452,77.622,336.1,78.046Z"
      transform="translate(-320.9 -70.375)"
      fill="#42b2fc"
    />
    <path
      id="Path_13"
      data-name="Path 13"
      d="M340.154,97.161c-9.359,1.648-21.625,5.886-28.519,10.14-.1-.221-.192-.445-.285-.669-.086-.208-.17-.416-.25-.628-.157-.41-.307-.829-.442-1.248-.022-.07-.045-.138-.067-.208,1.088-.7,2.3-1.4,3.626-2.074A86.961,86.961,0,0,1,339.7,94.293c.1.371.182.749.25,1.127a15.244,15.244,0,0,1,.2,1.571C340.148,97.049,340.154,97.1,340.154,97.161Z"
      transform="translate(-307.022 -82.809)"
      fill="#fff"
    />
    <path
      id="Path_14"
      data-name="Path 14"
      d="M341.853,104.041c0,.627-.032,1.255-.093,1.876-9.753,1.626-21.535,5.355-27.533,9.2a28.318,28.318,0,0,1-1.213-2.564,38.365,38.365,0,0,1,5.717-3.114,86.681,86.681,0,0,1,23.077-6.527C341.837,103.286,341.853,103.663,341.853,104.041Z"
      transform="translate(-308.685 -88.726)"
      fill="#0076c2"
      opacity="0.75"
    />
    <path
      id="Path_15"
      data-name="Path 15"
      d="M336.913,92.993l.421,1.8,5.049-4.659-6.511-1.6.384,1.643a86.839,86.839,0,0,0-26.562,8.342c-4.857,2.479-8.214,5.238-9.492,7.364-1.313,2.185-1.148,3.775-.616,4.965.886,1.983,3.339,2.776,5.7,2.775a18.653,18.653,0,0,0,5.258-.89s-2.465.261-4.17.245c-2.359-.023-6.507-1.4-4.074-5.456C305.6,102.029,324.388,94.931,336.913,92.993Z"
      transform="translate(-299.192 -78.855)"
      fill="#3cc4e7"
    />
  </svg>
)

export const DashBoardIcon = ({ isSelected }: any) => (
  <svg
    id="dashboard_1_"
    data-name="dashboard (1)"
    xmlns="http://www.w3.org/2000/svg"
    width="18.755"
    height="18.755"
    viewBox="0 0 18.755 18.755"
  >
    <path
      id="Path_12053"
      data-name="Path 12053"
      d="M7.229,6.252H1.368A1.369,1.369,0,0,1,0,4.884V1.368A1.369,1.369,0,0,1,1.368,0H7.229A1.369,1.369,0,0,1,8.6,1.368V4.884A1.369,1.369,0,0,1,7.229,6.252ZM1.368,1.172a.2.2,0,0,0-.2.2V4.884a.2.2,0,0,0,.2.2H7.229a.2.2,0,0,0,.2-.2V1.368a.2.2,0,0,0-.2-.2Zm0,0"
      transform="translate(0 0)"
      fill={isSelected ? "#009ef7" : colors.starkWhite}
    />
    <path
      id="Path_12054"
      data-name="Path 12054"
      d="M7.229,224.273H1.368A1.369,1.369,0,0,1,0,222.905V214.7a1.369,1.369,0,0,1,1.368-1.368H7.229A1.369,1.369,0,0,1,8.6,214.7v8.205A1.369,1.369,0,0,1,7.229,224.273ZM1.368,214.5a.2.2,0,0,0-.2.2v8.205a.2.2,0,0,0,.2.2H7.229a.2.2,0,0,0,.2-.2V214.7a.2.2,0,0,0-.2-.2Zm0,0"
      transform="translate(0 -205.517)"
      fill={isSelected ? "#009ef7" : colors.starkWhite}
    />
    <path
      id="Path_12055"
      data-name="Path 12055"
      d="M284.561,347.584H278.7a1.369,1.369,0,0,1-1.368-1.368V342.7a1.369,1.369,0,0,1,1.368-1.368h5.861a1.369,1.369,0,0,1,1.368,1.368v3.517A1.369,1.369,0,0,1,284.561,347.584ZM278.7,342.5a.2.2,0,0,0-.2.2v3.517a.2.2,0,0,0,.2.2h5.861a.2.2,0,0,0,.2-.2V342.7a.2.2,0,0,0-.2-.2Zm0,0"
      transform="translate(-267.173 -328.829)"
      fill={isSelected ? "#009ef7" : colors.starkWhite}
    />
    <path
      id="Path_12056"
      data-name="Path 12056"
      d="M284.561,10.941H278.7a1.369,1.369,0,0,1-1.368-1.368V1.368A1.369,1.369,0,0,1,278.7,0h5.861a1.369,1.369,0,0,1,1.368,1.368V9.573A1.369,1.369,0,0,1,284.561,10.941ZM278.7,1.172a.2.2,0,0,0-.2.2V9.573a.2.2,0,0,0,.2.2h5.861a.2.2,0,0,0,.2-.2V1.368a.2.2,0,0,0-.2-.2Zm0,0"
      transform="translate(-267.173 0)"
      fill={isSelected ? "#009ef7" : colors.starkWhite}
    />
  </svg>
)
export const UserManagementIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15.966"
    height="18.869"
    viewBox="0 0 15.966 18.869"
  >
    <g
      id="user_account_people_man"
      data-name="user, account, people, man"
      transform="translate(-5 -3)"
    >
      <path
        id="Path_12061"
        data-name="Path 12061"
        d="M18.6,16.1a.726.726,0,1,0-1.021,1.032,6.486,6.486,0,0,1,1.937,4.642c0,.888-2.544,2.177-6.531,2.177s-6.531-1.29-6.531-2.179a6.487,6.487,0,0,1,1.912-4.618.726.726,0,1,0-1.026-1.026A7.93,7.93,0,0,0,5,21.776C5,24.133,9.113,25.4,12.983,25.4s7.983-1.272,7.983-3.629A7.925,7.925,0,0,0,18.6,16.1Z"
        transform="translate(0 -3.536)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
      />
      <path
        id="Path_12062"
        data-name="Path 12062"
        d="M14.08,13.16A5.08,5.08,0,1,0,9,8.08,5.08,5.08,0,0,0,14.08,13.16Zm0-8.709A3.629,3.629,0,1,1,10.451,8.08,3.629,3.629,0,0,1,14.08,4.451Z"
        transform="translate(-1.097)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
      />
    </g>
  </svg>
)

export const MetricBlueprintIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22.658"
    height="22.549"
    viewBox="0 0 22.658 22.549"
  >
    <g
      id="analytics_3_"
      data-name="analytics (3)"
      transform="translate(0.027 0.1)"
    >
      <g
        id="Group_35266"
        data-name="Group 35266"
        transform="translate(2.268 2.195)"
      >
        <g id="Group_35265" data-name="Group 35265">
          <path
            id="Path_12481"
            data-name="Path 12481"
            d="M61.645,54.853l-.753-.188-.373-.9.4-.664a.435.435,0,0,0-.065-.532L59.694,51.41a.435.435,0,0,0-.532-.065l-.664.4-.9-.373-.187-.753a.435.435,0,0,0-.422-.33H55.348a.435.435,0,0,0-.422.33l-.187.753-.9.373-.664-.4a.435.435,0,0,0-.532.065l-1.159,1.159a.435.435,0,0,0-.065.532l.4.664-.373.9-.753.188a.435.435,0,0,0-.33.422v1.639a.435.435,0,0,0,.33.422l.753.188.373.9-.4.664a.435.435,0,0,0,.065.532l1.159,1.159a.435.435,0,0,0,.532.065l.664-.4.9.373.187.753a.435.435,0,0,0,.422.33h1.639a.435.435,0,0,0,.422-.33l.187-.753.9-.373.664.4a.435.435,0,0,0,.532-.065l1.159-1.159a.435.435,0,0,0,.065-.532l-.4-.664.373-.9.753-.188a.435.435,0,0,0,.33-.422V55.275A.435.435,0,0,0,61.645,54.853Zm-.54,1.721-.636.158a.435.435,0,0,0-.3.255l-.541,1.306a.435.435,0,0,0,.029.391l.337.561-.679.679-.561-.337a.435.435,0,0,0-.391-.029l-1.306.541a.435.435,0,0,0-.255.3l-.158.636h-.959L55.53,60.4a.435.435,0,0,0-.255-.3l-1.306-.541a.435.435,0,0,0-.391.029l-.561.337-.679-.679.337-.561a.435.435,0,0,0,.029-.391l-.541-1.306a.435.435,0,0,0-.3-.255l-.636-.158v-.959l.636-.158a.435.435,0,0,0,.3-.255L52.7,53.9a.435.435,0,0,0-.029-.391l-.337-.561.679-.679.561.337a.435.435,0,0,0,.391.029l1.306-.541a.435.435,0,0,0,.255-.3l.158-.636h.959l.158.636a.435.435,0,0,0,.255.3l1.306.541a.435.435,0,0,0,.391-.029l.561-.337.679.679-.337.561a.435.435,0,0,0-.029.391l.541,1.306a.435.435,0,0,0,.3.255l.636.158Z"
            transform="translate(-50.361 -50.288)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
            stroke={isSelected ? "#009ef7" : colors.starkWhite}
            strokeWidth="0.2"
          />
        </g>
      </g>
      <g
        id="Group_35268"
        data-name="Group 35268"
        transform="translate(5.031 4.958)"
      >
        <g id="Group_35267" data-name="Group 35267">
          <path
            id="Path_12482"
            data-name="Path 12482"
            d="M116.693,113.574a.435.435,0,0,0,0,.87,2.172,2.172,0,1,1-2.076,1.524.435.435,0,1,0-.83-.26,3.045,3.045,0,1,0,2.906-2.134Z"
            transform="translate(-113.649 -113.574)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
            stroke={isSelected ? "#009ef7" : colors.starkWhite}
            strokeWidth="0.2"
          />
        </g>
      </g>
      <g
        id="Group_35270"
        data-name="Group 35270"
        transform="translate(6.143 5.428)"
      >
        <g id="Group_35269" data-name="Group 35269">
          <path
            id="Path_12483"
            data-name="Path 12483"
            d="M139.931,124.541a.435.435,0,0,0-.605-.109l-.012.008a.435.435,0,1,0,.617.1Z"
            transform="translate(-139.137 -124.355)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
            stroke={isSelected ? "#009ef7" : colors.starkWhite}
            strokeWidth="0.2"
          />
        </g>
      </g>
      <g
        id="Group_35272"
        data-name="Group 35272"
        transform="translate(15.207 7.567)"
      >
        <g id="Group_35271" data-name="Group 35271">
          <path
            id="Path_12484"
            data-name="Path 12484"
            d="M347.208,173.349a.435.435,0,0,0-.435.435v.031a.435.435,0,0,0,.433.437h0a.435.435,0,0,0,.435-.433v-.034A.435.435,0,0,0,347.208,173.349Z"
            transform="translate(-346.773 -173.349)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
            stroke={isSelected ? "#009ef7" : colors.starkWhite}
            strokeWidth="0.2"
          />
        </g>
      </g>
      <g id="Group_35274" data-name="Group 35274" transform="translate(0.073)">
        <g id="Group_35273" data-name="Group 35273" transform="translate(0)">
          <path
            id="Path_12485"
            data-name="Path 12485"
            d="M21.9,19.307l-5.159-5.159a.435.435,0,0,0-.574-.035l-1.528-1.528a7.969,7.969,0,0,0,1.18-2.556.435.435,0,1,0-.842-.22,7.132,7.132,0,1,1-.014-3.665.435.435,0,0,0,.84-.226,8,8,0,1,0-3.119,8.626l1.523,1.523a.435.435,0,0,0,.006.608l2.073,2.073H15.033a.435.435,0,0,0-.435.435V21.4H13.554V16.964a.435.435,0,0,0-.435-.435H10.336a.435.435,0,0,0-.435.435V21.4H8.858V18.791a.435.435,0,0,0-.435-.435H5.639a.435.435,0,0,0-.435.435V21.4H4.161V16.066a.435.435,0,0,0-.435-.435H.943a.435.435,0,0,0-.435.435V21.4a.435.435,0,0,0,0,.87H18.773a.434.434,0,0,0,.366-.669l.233.233a1.755,1.755,0,0,0,1.25.515h.009A1.788,1.788,0,0,0,21.9,19.307ZM3.291,21.4H1.378V16.5H3.291Zm4.7,0H6.074V19.225H7.988Zm4.7,0H10.771v-4h1.913Zm.687-7.4a8.021,8.021,0,0,0,.726-.728l1.451,1.451-.727.727Zm2.486,3.087-.721-.721,1.3-1.3.721.721ZM17.381,21.4H15.468V19.618H17.12l.034,0,.227.227Zm1.392,0h-.522v-.687l.756.756A.432.432,0,0,0,18.773,21.4Zm2.5-.19a.917.917,0,0,1-.648.27h0a.891.891,0,0,1-.635-.261L16.472,17.7l1.3-1.3,3.516,3.515A.912.912,0,0,1,21.276,21.21Z"
            transform="translate(-0.073)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
            stroke={isSelected ? "#009ef7" : colors.starkWhite}
            strokeWidth="0.2"
          />
        </g>
      </g>
    </g>
  </svg>
)
export const MetricGoalsIcon = ({ isSelected }: any) => (
  <svg
    id="success_1_"
    data-name="success (1)"
    xmlns="http://www.w3.org/2000/svg"
    width="22.518"
    height="22.518"
    viewBox="0 0 22.518 22.518"
  >
    <path
      id="Path_12496"
      data-name="Path 12496"
      d="M22.518,6.623,20.034,3.311,22.518,0h-8.61V11.921H11.921v1.325H7.947V14.57H3.974v1.325H0v6.623H17.219v-10.6H15.232v-5.3ZM9.272,14.57h2.649v6.623H9.272ZM5.3,15.895H7.947v5.3H5.3ZM1.325,17.219H3.974v3.974H1.325Zm14.57,3.974H13.246V13.246h2.649ZM15.232,1.325h4.636l-1.49,1.987L19.868,5.3H15.232Z"
      fill={isSelected ? "#009ef7" : colors.starkWhite}
    />
    <path
      id="Path_12497"
      data-name="Path 12497"
      d="M30.064,93.94l1.4,1.4,3.144-3.1v1.285h1.325V90H32.406v1.325h1.251l-2.184,2.151-1.408-1.408L24.43,97.7l.937.937Z"
      transform="translate(-23.351 -86.026)"
      fill={isSelected ? "#009ef7" : colors.starkWhite}
    />
  </svg>
)

export const MetricsIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18.846"
    height="18.407"
    viewBox="0 0 18.846 18.407"
  >
    <g id="Icon" transform="translate(-1.25 -1.5)">
      <path
        id="Path_12067"
        data-name="Path 12067"
        d="M19.438,21H1.907a.657.657,0,0,0,0,1.315H19.438a.657.657,0,0,0,0-1.315Z"
        transform="translate(0 -2.407)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12068"
        data-name="Path 12068"
        d="M7.071,16.534A1.535,1.535,0,0,0,5.537,15H3.784A1.535,1.535,0,0,0,2.25,16.534v4.383a.658.658,0,0,0,.657.657H6.414a.658.658,0,0,0,.657-.657Zm-1.315,0v3.725H3.565V16.534a.218.218,0,0,1,.219-.219H5.537a.218.218,0,0,1,.219.219Z"
        transform="translate(-0.123 -1.667)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12069"
        data-name="Path 12069"
        d="M14.071,20.423V12.534A1.534,1.534,0,0,0,12.537,11H10.784A1.534,1.534,0,0,0,9.25,12.534v7.889a.658.658,0,0,0,.657.657h3.506A.658.658,0,0,0,14.071,20.423Zm-1.315-7.889v7.231H10.565V12.534a.219.219,0,0,1,.219-.219h1.753A.219.219,0,0,1,12.756,12.534Z"
        transform="translate(-0.988 -1.173)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12070"
        data-name="Path 12070"
        d="M21.071,14.534A1.534,1.534,0,0,0,19.537,13H17.784a1.534,1.534,0,0,0-1.534,1.534V20.67a.658.658,0,0,0,.657.657h3.506a.658.658,0,0,0,.657-.657Zm-1.315,0v5.478H17.565V14.534a.219.219,0,0,1,.219-.219h1.753A.219.219,0,0,1,19.756,14.534Z"
        transform="translate(-1.852 -1.42)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12071"
        data-name="Path 12071"
        d="M6.694,9.472a1.972,1.972,0,1,0-1.972,1.972A1.973,1.973,0,0,0,6.694,9.472Zm-1.315,0a.657.657,0,1,1-.657-.657A.658.658,0,0,1,5.38,9.472Z"
        transform="translate(-0.185 -0.741)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12072"
        data-name="Path 12072"
        d="M13.694,3.472a1.972,1.972,0,1,0-1.972,1.972A1.973,1.973,0,0,0,13.694,3.472Zm-1.315,0a.657.657,0,1,1-.657-.657A.658.658,0,0,1,12.38,3.472Z"
        transform="translate(-1.049)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12073"
        data-name="Path 12073"
        d="M20.694,7.472a1.972,1.972,0,1,0-1.972,1.972A1.973,1.973,0,0,0,20.694,7.472Zm-1.315,0a.657.657,0,1,1-.657-.657A.658.658,0,0,1,19.38,7.472Z"
        transform="translate(-1.914 -0.494)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12074"
        data-name="Path 12074"
        d="M9.479,4.458,5.991,7.438a.657.657,0,0,0,.854,1l3.489-2.98a.657.657,0,0,0-.854-1Z"
        transform="translate(-0.557 -0.346)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
      <path
        id="Path_12075"
        data-name="Path 12075"
        d="M17.06,5.847,13.974,4.077a.657.657,0,1,0-.654,1.14l3.085,1.771a.657.657,0,0,0,.654-1.14Z"
        transform="translate(-1.449 -0.307)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        fillRule="evenodd"
      />
    </g>
  </svg>
)

export const OpportunityIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="23.343"
    height="23.41"
    viewBox="0 0 23.343 23.41"
  >
    <g
      id="Group_35278"
      data-name="Group 35278"
      transform="translate(-35.389 -521.869)"
    >
      <g id="job-search" transform="translate(25.156 512.369)">
        <ellipse
          id="Ellipse_514"
          data-name="Ellipse 514"
          cx="10.182"
          cy="10.226"
          rx="10.182"
          ry="10.226"
          transform="translate(10.733 10)"
          fill="none"
          stroke={isSelected ? "#009ef7" : colors.starkWhite}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1"
        />
        <line
          id="Line_1356"
          data-name="Line 1356"
          x2="1.63"
          y2="1.637"
          transform="translate(28.186 27.528)"
          fill="none"
          stroke={isSelected ? "#009ef7" : colors.starkWhite}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1"
        />
        <path
          id="Path_12489"
          data-name="Path 12489"
          d="M419.124,418.658l-1.446,1.452-2.474-2.456,1.446-1.452Z"
          transform="translate(-386.048 -387.7)"
          fill="none"
          stroke={isSelected ? "#009ef7" : colors.starkWhite}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1"
        />
      </g>
      <g
        id="Group_35277"
        data-name="Group 35277"
        transform="translate(-17 16.355)"
      >
        <path
          id="Path_12498"
          data-name="Path 12498"
          d="M0,0H1V2.01H0Z"
          transform="translate(59.029 512.094) rotate(-43.38)"
          fill={isSelected ? "#009ef7" : colors.starkWhite}
        />
        <g
          id="Group_35276"
          data-name="Group 35276"
          transform="translate(57.741 508.246)"
        >
          <path
            id="Path_12492"
            data-name="Path 12492"
            d="M34.623,23.557l-3.557-.5-1.588-3.235a.5.5,0,0,0-.9,0l-1.588,3.22-3.557.5a.5.5,0,0,0-.281.859l2.577,2.512-.608,3.517a.5.5,0,0,0,.728.533l3.18-1.648,3.18,1.673a.563.563,0,0,0,.236.055.5.5,0,0,0,.5-.588l-.608-3.517,2.577-2.512a.5.5,0,0,0-.281-.859Zm-3.185,2.834a.5.5,0,0,0-.146.442l.5,2.8L29.282,28.31a.533.533,0,0,0-.472,0L26.3,29.631l.5-2.8a.5.5,0,0,0-.146-.442L24.62,24.406,27.429,24a.5.5,0,0,0,.382-.276l1.216-2.547,1.256,2.547a.5.5,0,0,0,.382.276l2.808.407Z"
            transform="translate(-22.995 -15.75)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
          <path
            id="Path_12493"
            data-name="Path 12493"
            d="M34,12h1v2.01H34Z"
            transform="translate(-28.465 -11.346)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
          <path
            id="Path_12494"
            data-name="Path 12494"
            d="M0,0H2.01V1H0Z"
            transform="translate(8.687 4.126) rotate(-46.62)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
        </g>
      </g>
    </g>
  </svg>
)

export const PnLBlueprintIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    //xmlns:xlink="http://www.w3.org/1999/xlink"
    width="21.103"
    height="21.103"
    viewBox="0 0 21.103 21.103"
  >
    <defs>
      <clipPath id="clipPath">
        <path
          id="path1175"
          d="M0-682.665H21.1v21.1H0Z"
          transform="translate(0 682.665)"
        />
      </clipPath>
    </defs>
    <g id="g1161" transform="translate(0 682.665)">
      <g id="g1163" transform="translate(13.272 -672.517)">
        <path
          id="path1165"
          d="M-116.29,0V4.69h1.236l-2.473,4.122L-120,4.69h1.236V0"
          transform="translate(120)"
          fill="none"
          stroke={isSelected ? "#009ef7" : colors.starkWhite}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1"
        />
      </g>
      <g id="g1167" transform="translate(2.885 -680.522)">
        <path
          id="path1169"
          d="M-38.763-276.228v-4.69H-40l2.473-4.122,2.473,4.122H-36.29v4.69"
          transform="translate(40 285.039)"
          fill="none"
          stroke={isSelected ? "#009ef7" : colors.starkWhite}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1"
        />
      </g>
      <g id="g1171" transform="translate(0 -682.665)">
        <g id="g1173" clipPath="url(#clipPath)">
          <g id="g1179" transform="translate(4.575 14.129)">
            <path
              id="path1181"
              d="M-3.463-90.448a.823.823,0,0,0,.714.412.824.824,0,0,0,.824-.824.824.824,0,0,0-.824-.824.824.824,0,0,1-.824-.824.824.824,0,0,1,.824-.824.823.823,0,0,1,.714.412"
              transform="translate(3.573 93.333)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1183" transform="translate(5.399 13.321)">
            <path
              id="path1185"
              d="M0,0V.808"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1187" transform="translate(5.399 17.426)">
            <path
              id="path1189"
              d="M0,0V.791"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1191" transform="translate(14.961 3.742)">
            <path
              id="path1193"
              d="M-3.463-90.448a.823.823,0,0,0,.714.412.824.824,0,0,0,.824-.824.824.824,0,0,0-.824-.824.824.824,0,0,1-.824-.824.824.824,0,0,1,.824-.824.823.823,0,0,1,.714.412"
              transform="translate(3.573 93.333)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1195" transform="translate(15.786 2.935)">
            <path
              id="path1197"
              d="M0,0V.808"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1199" transform="translate(15.786 7.04)">
            <path
              id="path1201"
              d="M0,0V.791"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1203" transform="translate(4.946 20.278)">
            <path
              id="path1205"
              d="M-25.842-12.921a.412.412,0,0,1-.412.412.412.412,0,0,1-.412-.412.412.412,0,0,1,.412-.412.412.412,0,0,1,.412.412"
              transform="translate(26.667 13.333)"
            />
          </g>
          <g id="g1207" transform="translate(15.332 0)">
            <path
              id="path1209"
              d="M-25.842-12.921a.412.412,0,0,1-.412.412.412.412,0,0,1-.412-.412.412.412,0,0,1,.412-.412.412.412,0,0,1,.412.412"
              transform="translate(26.667 13.333)"
            />
          </g>
          <g id="g1211" transform="translate(10.799 0.759)">
            <path
              id="path1213"
              d="M-212.181,0a4.948,4.948,0,0,1,3.124,4.6,4.948,4.948,0,0,1-3.709,4.79A4.951,4.951,0,0,1-214,9.545a4.951,4.951,0,0,1-1.236-.156,4.948,4.948,0,0,1-3.709-4.79A4.948,4.948,0,0,1-215.825,0"
              transform="translate(218.949)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g1215" transform="translate(0.412 10.799)">
            <path
              id="path1217"
              d="M-97.927-299.243a4.948,4.948,0,0,1-3.124-4.6,4.949,4.949,0,0,1,3.709-4.79,4.951,4.951,0,0,1,1.236-.156,4.951,4.951,0,0,1,1.236.156,4.949,4.949,0,0,1,3.709,4.79,4.948,4.948,0,0,1-3.124,4.6"
              transform="translate(101.05 308.789)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
        </g>
      </g>
    </g>
  </svg>
)
export const PnLReportingIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19.936"
    height="22.976"
    viewBox="0 0 19.936 22.976"
  >
    <path
      id="stats"
      d="M58.933,19.788l-1.254-.962a.318.318,0,0,0-.388,0l-1.254.962a.318.318,0,0,0-.125.253V31.158a.318.318,0,0,0,.318.318h2.508a.318.318,0,0,0,.318-.318V20.04a.318.318,0,0,0-.125-.253Zm-.512,11.052H56.55V20.2l.935-.718.936.718Zm-3.984-8.845-1.254-.962a.318.318,0,0,0-.388,0l-1.254.962a.318.318,0,0,0-.125.253v8.911a.318.318,0,0,0,.318.318h2.508a.318.318,0,0,0,.318-.318V22.247a.319.319,0,0,0-.125-.253Zm-.512,8.845H52.053V22.4l.935-.718.936.718ZM49.94,24.647l-1.254-.962a.318.318,0,0,0-.388,0l-1.254.962a.318.318,0,0,0-.125.253v6.258a.318.318,0,0,0,.318.318h2.508a.318.318,0,0,0,.318-.318V24.9a.318.318,0,0,0-.125-.253Zm-.512,6.193H47.556V25.056l.936-.718.936.718Zm-3.984-3.886-1.254-.962a.318.318,0,0,0-.388,0l-1.254.962a.319.319,0,0,0-.125.253v3.952a.318.318,0,0,0,.318.318h2.508a.318.318,0,0,0,.318-.318V27.205a.318.318,0,0,0-.125-.253Zm-.512,3.886H43.06V27.363L44,26.645l.936.718Zm-2.488-7.563a.318.318,0,0,1,.188-.409l.409-.154a48.555,48.555,0,0,0,9.433-4.772l-.609-.033a.318.318,0,1,1,.035-.636l1.558.085a.318.318,0,0,1,.3.357l-.19,1.542a.318.318,0,1,1-.632-.078l.1-.846a49.065,49.065,0,0,1-9.772,4.976l-.413.155a.318.318,0,0,1-.409-.188ZM57.485,9a4.372,4.372,0,1,0,4.372,4.372A4.372,4.372,0,0,0,57.485,9Zm0,8.107a3.735,3.735,0,1,1,3.735-3.735,3.735,3.735,0,0,1-3.735,3.735Zm1.222-3.532a1.042,1.042,0,0,1,.19,1.057,1.263,1.263,0,0,1-1.055.753h-.014v.321a.318.318,0,1,1-.637,0v-.343a1.419,1.419,0,0,1-1.178-1.08.318.318,0,0,1,.621-.143.94.94,0,0,0,1.127.611A.649.649,0,0,0,58.3,14.4a.4.4,0,0,0-.086-.417.917.917,0,0,0-.678-.3,2.785,2.785,0,0,1-.568-.129l-.01,0a1.132,1.132,0,0,1-.787-1.049,1.168,1.168,0,0,1,.8-1.056,1.687,1.687,0,0,1,.216-.064v-.348a.318.318,0,1,1,.637,0v.34a1.517,1.517,0,0,1,1.1.987.318.318,0,1,1-.6.226.847.847,0,0,0-1.136-.543.546.546,0,0,0-.385.468c0,.186.134.344.385.447a2.241,2.241,0,0,0,.438.1A1.516,1.516,0,0,1,58.707,13.576Z"
      transform="translate(-42.171 -8.75)"
      fill={colors.starkWhite}
      stroke={isSelected ? "#009ef7" : colors.starkWhite}
      strokeWidth="0.5"
    />
  </svg>
)
export const CustomerManagementIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19.894"
    height="16.496"
    viewBox="0 0 19.894 16.496"
  >
    <g id="group_1_" data-name="group (1)" transform="translate(0 -43.729)">
      <g
        id="Group_34920"
        data-name="Group 34920"
        transform="translate(0 50.954)"
      >
        <g id="Group_34919" data-name="Group 34919" transform="translate(0 0)">
          <path
            id="Path_12057"
            data-name="Path 12057"
            d="M16.98,231.175H15.032a2.907,2.907,0,0,0-.766.1,2.917,2.917,0,0,0-2.6-1.6H8.226a2.917,2.917,0,0,0-2.6,1.6,2.907,2.907,0,0,0-.766-.1H2.914A2.917,2.917,0,0,0,0,234.089V237.2a1.75,1.75,0,0,0,1.748,1.749h16.4a1.75,1.75,0,0,0,1.748-1.749v-3.113A2.917,2.917,0,0,0,16.98,231.175ZM5.312,232.594v5.191H1.748a.583.583,0,0,1-.583-.583v-3.113a1.75,1.75,0,0,1,1.748-1.748H4.861a1.743,1.743,0,0,1,.457.061C5.315,232.465,5.312,232.529,5.312,232.594Zm8.1,5.191H6.477v-5.191a1.75,1.75,0,0,1,1.748-1.748h3.442a1.75,1.75,0,0,1,1.748,1.748Zm5.312-.583a.583.583,0,0,1-.583.583H14.582v-5.191c0-.065,0-.129-.007-.193a1.745,1.745,0,0,1,.457-.061H16.98a1.75,1.75,0,0,1,1.748,1.749Z"
            transform="translate(0 -229.68)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
        </g>
      </g>
      <g
        id="Group_34922"
        data-name="Group 34922"
        transform="translate(1.298 46.945)"
      >
        <g id="Group_34921" data-name="Group 34921" transform="translate(0 0)">
          <path
            id="Path_12058"
            data-name="Path 12058"
            d="M36,126.5a2.59,2.59,0,1,0,2.59,2.59A2.592,2.592,0,0,0,36,126.5Zm0,4.013a1.424,1.424,0,1,1,1.424-1.424A1.425,1.425,0,0,1,36,130.517Z"
            transform="translate(-33.414 -126.504)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
        </g>
      </g>
      <g
        id="Group_34924"
        data-name="Group 34924"
        transform="translate(6.487 43.729)"
      >
        <g id="Group_34923" data-name="Group 34923" transform="translate(0 0)">
          <path
            id="Path_12059"
            data-name="Path 12059"
            d="M170.422,43.729a3.46,3.46,0,1,0,3.46,3.46A3.463,3.463,0,0,0,170.422,43.729Zm0,5.754a2.294,2.294,0,1,1,2.294-2.294A2.3,2.3,0,0,1,170.422,49.483Z"
            transform="translate(-166.962 -43.729)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
        </g>
      </g>
      <g
        id="Group_34926"
        data-name="Group 34926"
        transform="translate(13.416 46.945)"
      >
        <g id="Group_34925" data-name="Group 34925" transform="translate(0 0)">
          <path
            id="Path_12060"
            data-name="Path 12060"
            d="M347.884,126.5a2.59,2.59,0,1,0,2.59,2.59A2.592,2.592,0,0,0,347.884,126.5Zm0,4.013a1.424,1.424,0,1,1,1.424-1.424A1.426,1.426,0,0,1,347.884,130.517Z"
            transform="translate(-345.294 -126.504)"
            fill={isSelected ? "#009ef7" : colors.starkWhite}
          />
        </g>
      </g>
    </g>
  </svg>
)

export const ProvidesIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    //xmlns:xlink="http://www.w3.org/1999/xlink"
    width="19.407"
    height="19.407"
    viewBox="0 0 19.407 19.407"
  >
    <defs>
      <clipPath id="clipPath">
        <path
          id="path2928"
          d="M0-682.665H19.407v19.407H0Z"
          transform="translate(0 682.665)"
        />
      </clipPath>
    </defs>
    <g id="g2922" transform="translate(0 682.665)">
      <g id="g2924" transform="translate(0 -682.665)">
        <g id="g2926" clipPath="url(#clipPath)">
          <g id="g2932" transform="translate(5.441 0.569)">
            <path
              id="path2934"
              d="M-277.148-64.271a4.7,4.7,0,0,0-3.761-1.881h-.171a4.7,4.7,0,0,0-3.761,1.881h0a2.082,2.082,0,0,0-.416,1.249v2.351a4.263,4.263,0,0,0,4.263,4.263,4.263,4.263,0,0,0,4.263-4.263v-2.351a2.082,2.082,0,0,0-.416-1.249Z"
              transform="translate(285.258 66.152)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2936" transform="translate(5.441 4.223)">
            <path
              id="path2938"
              d="M0-41.626H0a1.47,1.47,0,0,0,1.04-.431l.787-.787h0a5.447,5.447,0,0,0,4.872,0h0l.787.787a1.47,1.47,0,0,0,1.04.431h0"
              transform="translate(0 42.844)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2940" transform="translate(0.569 12.14)">
            <path
              id="path2942"
              d="M-624.395-228.944l-1.282-3.845a3.045,3.045,0,0,0-2.228-2.01l-3.8-.844-1.827,3.045-1.827-3.045-3.8.844a3.045,3.045,0,0,0-2.228,2.01l-1.282,3.845"
              transform="translate(642.665 235.643)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2944" transform="translate(7.877 9.996)">
            <path
              id="path2946"
              d="M0-73.25v-2.143"
              transform="translate(0 75.393)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2948" transform="translate(11.531 9.996)">
            <path
              id="path2950"
              d="M0-73.25v-2.143"
              transform="translate(0 75.393)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2952" transform="translate(12.14 15.185)">
            <path
              id="path2954"
              d="M-20.814-124.879h-.609v-1.827A1.827,1.827,0,0,1-20.887-128a1.827,1.827,0,0,1,1.292-.535,1.827,1.827,0,0,1,1.827,1.827v1.827h-.609"
              transform="translate(21.423 128.533)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2956" transform="translate(13.967 12.681)">
            <path
              id="path2958"
              d="M0-64.752v-1.895"
              transform="translate(0 66.646)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2960" transform="translate(4.223 16.403)">
            <path
              id="path2962"
              d="M-41.626,0a1.219,1.219,0,0,0-1.218,1.218,1.219,1.219,0,0,0,1.218,1.218,1.219,1.219,0,0,0,1.218-1.218A1.219,1.219,0,0,0-41.626,0Z"
              transform="translate(42.844)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g2964" transform="translate(5.441 12.681)">
            <path
              id="path2966"
              d="M0-127.193v-3.722"
              transform="translate(0 130.914)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
        </g>
      </g>
    </g>
  </svg>
)
export const ResourceBudgetIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    //xmlns:xlink="http://www.w3.org/1999/xlink"
    width="21.104"
    height="21.103"
    viewBox="0 0 21.104 21.103"
  >
    <defs>
      <clipPath id="clipPath">
        <path
          id="path3252"
          d="M0-682.665H21.1v21.1H0Z"
          transform="translate(0 682.665)"
        />
      </clipPath>
    </defs>
    <g id="g3246" transform="translate(0 682.665)">
      <g id="g3248" transform="translate(0 -682.665)">
        <g id="g3250" transform="translate(0 0)" clipPath="url(#clipPath)">
          <g id="g3256" transform="translate(2.675 12.815)">
            <path
              id="path3258"
              d="M-167.667-123.317a1.521,1.521,0,0,0,1.445,1.4,1.51,1.51,0,0,0,1.083-.391v2.931H-171.9a1.119,1.119,0,0,1-1.119-1.12v-6.757h7.877v2.7a1.51,1.51,0,0,0-1.017-.392A1.515,1.515,0,0,0-167.667-123.317Z"
              transform="translate(173.016 127.25)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g3260" transform="translate(8.02 10.395)">
            <path
              id="path3262"
              d="M-257.878-322.769h1.063a1.119,1.119,0,0,0,1.119-1.119v-6.757h-2.755a1.51,1.51,0,0,0,.3-.995,1.521,1.521,0,0,0-1.405-1.421,1.515,1.515,0,0,0-1.623,1.511,1.508,1.508,0,0,0,.3.9h-2.69v2.7a1.511,1.511,0,0,0-1.087-.39,1.521,1.521,0,0,0-1.441,1.406,1.515,1.515,0,0,0,1.512,1.623,1.51,1.51,0,0,0,1.017-.392v2.93h2.052"
              transform="translate(266.105 333.065)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g3264" transform="translate(4.982 0.412)">
            <path
              id="path3266"
              d="M-199.412-10.278l4.778,4.778-1.948,1.948a1.512,1.512,0,0,0-.493-.913,1.521,1.521,0,0,0-2-.012,1.515,1.515,0,0,0-.079,2.217,1.509,1.509,0,0,0,.851.428l-1.9,1.9-1.987-1.987a1.508,1.508,0,0,1-.441,1,1.518,1.518,0,0,1-2.147,0,1.518,1.518,0,0,1,0-2.147,1.507,1.507,0,0,1,1-.441L-205.773-5.5l4.778-4.778A1.12,1.12,0,0,1-199.412-10.278Z"
              transform="translate(205.773 10.606)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
          <g id="g3268" transform="translate(14.443 20.691)">
            <path
              id="path3270"
              d="M0,0H0"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1"
            />
          </g>
        </g>
      </g>
    </g>
  </svg>
)

export const OfficeHuddleIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21.599"
    height="18.223"
    viewBox="0 0 21.599 18.223"
  >
    <g id="support" transform="translate(-0.794 -3.512)">
      <path
        id="Path_12500"
        data-name="Path 12500"
        d="M21.881,93.219l-.746-.744a1.414,1.414,0,0,0-1.674-.239l-1.406-1.4.068-.068a1.466,1.466,0,0,0,0-2.07,1.45,1.45,0,0,0-.735-.4,1.475,1.475,0,0,0,.058-.411,1.463,1.463,0,0,0-1.164-1.433,1.475,1.475,0,0,0,.058-.411,1.461,1.461,0,0,0-1.163-1.432,1.462,1.462,0,0,0-1.562-1.866l-2.786-.527a1.505,1.505,0,0,0-1.291.172.761.761,0,0,0-.064.066,1.469,1.469,0,0,0-1.541.339,1.453,1.453,0,0,0-.428,1.035,1.477,1.477,0,0,0,.058.411A1.463,1.463,0,0,0,6.4,85.67a1.473,1.473,0,0,0,.058.412,1.465,1.465,0,0,0-.908,2.26,1.464,1.464,0,0,0-.562,2.421l.068.068-1.406,1.4a1.412,1.412,0,0,0-1.6.277l-.747.745a1.411,1.411,0,0,0,0,1.993l.932.931a.422.422,0,0,0,.6-.6L1.9,94.652a.566.566,0,0,1,0-.8l.747-.745a.566.566,0,0,1,.689-.084.427.427,0,0,0,.053.065.42.42,0,0,0,.121.084l4.282,4.276a.416.416,0,0,0,.2.206.565.565,0,0,1-.023.771l-.746.745a.567.567,0,0,1-.8,0L5.5,98.242a.422.422,0,0,0-.6.6l.932.933a1.414,1.414,0,0,0,1.992,0l.746-.746a1.412,1.412,0,0,0,.2-1.744L10.352,95.7l.005-.005c.181-.187.686-.152,1.174-.118a6.075,6.075,0,0,0,1.111.007l1.733,1.731a1.412,1.412,0,0,0,.24,1.672l.746.746a1.412,1.412,0,0,0,1.992,0l4.527-4.522a1.411,1.411,0,0,0,0-1.993ZM10.672,83.046l1.832.347-1.1,1.1a2.663,2.663,0,0,1-.657-.478,1.943,1.943,0,0,1-.622-.96h0a.369.369,0,0,1,0-.042,1.431,1.431,0,0,1,.545.037ZM8.53,83.39a.623.623,0,0,1,.788-.075,3.021,3.021,0,0,0,.848,1.306,3.128,3.128,0,0,0,1.217.754.62.62,0,0,1-.948.792h0l-1.9-1.9a.622.622,0,0,1,0-.875Zm-1.1,1.843a.623.623,0,0,1,.878,0l1.534,1.531a.621.621,0,0,1-.879.877h0L7.8,86.479l-.006-.005-.364-.364a.62.62,0,0,1,0-.876ZM6.321,87.076a.625.625,0,0,1,.875,0L8.363,88.24a.62.62,0,0,1-.878.875l-.423-.423h0l-.74-.739a.621.621,0,0,1,0-.877ZM5.585,89.29a.624.624,0,0,1,.879,0l.424.423a.62.62,0,0,1-.879.875l-.053-.053,0,0,0,0-.366-.365a.62.62,0,0,1,0-.876Zm8.608,4.8a.422.422,0,0,0-.6,0l-.376.375c-.357.357-1,.312-1.63.269a2.233,2.233,0,0,0-1.837.371L8.2,96.659,4.307,92.774,5.683,91.4a1.465,1.465,0,0,0,2.23-1.249s0-.007,0-.011h.011a1.461,1.461,0,0,0,1.464-1.463s0-.007,0-.011H9.4a1.459,1.459,0,0,0,1.464-1.474h.011a1.465,1.465,0,0,0,1.25-2.227l1.207-1.206a.62.62,0,0,1,.317-.169l.037-.006a.619.619,0,0,1,.526,1.051L13.844,85l0,0-.423.423a.422.422,0,0,0,.6.6l.369-.368h0l.056-.056a.622.622,0,0,1,.877,0,.62.62,0,0,1,0,.876l-.37.37h0l-.423.422a.422.422,0,0,0,.6.6l.425-.423a.62.62,0,0,1,.877.876l-.737.737a.422.422,0,1,0,.6.6l.361-.361.006-.006a.62.62,0,0,1,.878.876l-.367.367h0l-.583.582a.422.422,0,0,0,.6.6l.285-.285,1.366,1.365-3.89,3.886-1.394-1.392a1.661,1.661,0,0,0,.277-.223l.376-.375a.422.422,0,0,0,0-.6Zm7.091.523-4.527,4.522a.568.568,0,0,1-.8,0l-.746-.746a.565.565,0,0,1,0-.8l4.527-4.522a.568.568,0,0,1,.8,0l.746.744a.566.566,0,0,1,0,.8Z"
        transform="translate(0 -78.547)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        stroke={isSelected ? "#009ef7" : "#9b999a"}
        strokeWidth="0.2"
      />
      <path
        id="Path_12503"
        data-name="Path 12503"
        d="M60.051,430.32a.422.422,0,0,0,.005.6h0a.422.422,0,1,0-.006-.6Z"
        transform="translate(-56.56 -411.989)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        stroke={isSelected ? "#009ef7" : "#9b999a"}
        strokeWidth="0.2"
      />
      <path
        id="Path_12504"
        data-name="Path 12504"
        d="M332.933,332.963a.422.422,0,1,0-.19-.8h0a.422.422,0,0,0,.192.8Z"
        transform="translate(-317.707 -318.027)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
        stroke={isSelected ? "#009ef7" : "#9b999a"}
        strokeWidth="0.2"
      />
    </g>
  </svg>
)

export const AuditIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19.5"
    height="19.5"
    viewBox="0 0 19.5 19.5"
  >
    <g id="audit" transform="translate(-2.25 -2.25)">
      <path
        id="Path_12505"
        data-name="Path 12505"
        d="M7.75,13a.75.75,0,0,1-.75.75H6a.75.75,0,0,1,0-1.5H7A.75.75,0,0,1,7.75,13ZM8,9.25H6a.75.75,0,0,0,0,1.5H8a.75.75,0,0,0,0-1.5ZM6,7.75h5a.75.75,0,0,0,0-1.5H6a.75.75,0,0,0,0,1.5ZM16.548,11.4l-2.214,1.668-.882-.667a.75.75,0,1,0-.9,1.2l1.333,1.01a.745.745,0,0,0,.453.152.756.756,0,0,0,.452-.151L17.452,12.6a.75.75,0,0,0-.9-1.2ZM21.53,18.53a.748.748,0,0,1-1.06,0l-1.437-1.437a5.818,5.818,0,0,1-1.282.952V19A2.752,2.752,0,0,1,15,21.75H5A2.752,2.752,0,0,1,2.25,19V5A2.752,2.752,0,0,1,5,2.25H15A2.752,2.752,0,0,1,17.75,5V7.952A5.732,5.732,0,0,1,19.958,15.9L21.53,17.47a.75.75,0,0,1,0,1.061Zm-5.28.078a5.75,5.75,0,1,1,0-11.218V5A1.252,1.252,0,0,0,15,3.749H5A1.252,1.252,0,0,0,3.75,5V19A1.252,1.252,0,0,0,5,20.249H15A1.252,1.252,0,0,0,16.25,19Zm3-5.609A4.25,4.25,0,1,0,15,17.249,4.255,4.255,0,0,0,19.25,13Z"
        fill={colors.starkWhite}
      />
    </g>
  </svg>
)

export const ProntoAiIcon = ({ isSelected }: any) => (
  <svg
    id="Layer_1"
    width="25"
    height="25"
    viewBox="0 0 512 512"
    xmlns="http://www.w3.org/2000/svg"
    data-name="Layer 1"
  >
    <path
      fill={colors.primaryOrange}
      strokeWidth="1.2"
      d="m373.412 175.568c-7.619-7.753-17.239-12.869-27.711-14.853-7.917-19.625-27.277-33.121-48.965-33.121-16.389 0-31.054 7.521-40.734 19.292-9.68-11.772-24.345-19.292-40.734-19.292-21.688 0-41.049 13.496-48.965 33.121-10.472 1.983-20.092 7.099-27.711 14.853-9.75 9.922-15.12 23.049-15.12 36.963 0 5.594.887 11.114 2.608 16.375-14.237 9.662-23.188 25.956-23.188 43.663s8.951 34.025 23.188 43.69c-1.722 5.262-2.609 10.782-2.609 16.377 0 25.498 18.531 47.104 42.827 51.797 7.915 19.619 27.279 33.11 48.969 33.11 16.389 0 31.055-7.517 40.734-19.282 9.679 11.766 24.345 19.282 40.734 19.282 21.69 0 41.055-13.492 48.969-33.11 24.295-4.693 42.827-26.299 42.827-51.797 0-5.594-.887-11.115-2.609-16.377 14.237-9.665 23.188-25.97 23.188-43.69s-8.951-34.001-23.188-43.663c1.721-5.261 2.608-10.781 2.608-16.375 0-13.914-5.37-27.041-15.12-36.963zm-158.145 217.975c-13.475 0-25.302-9.543-28.123-22.69-1.168-5.447-5.932-9.374-11.502-9.481-15.534-.299-28.171-13.19-28.171-28.736 0-5.422 1.534-10.723 4.437-15.33 3.84-6.063 9.712-10.446 16.535-12.338 6.386-1.772 10.127-8.385 8.356-14.771-1.772-6.387-8.385-10.127-14.771-8.356-8.958 2.485-17.006 7.29-23.441 13.858-7.204-5.332-11.695-13.869-11.695-23.13 0-12.056 7.617-22.912 18.954-27.011 3.417-1.236 6.09-3.953 7.269-7.389 1.18-3.437.738-7.223-1.2-10.296-2.907-4.61-4.443-9.915-4.443-15.341 0-15.547 12.638-28.438 28.171-28.736 5.572-.107 10.336-4.036 11.503-9.485 2.819-13.162 14.646-22.715 28.122-22.715 15.844 0 28.734 12.903 28.734 28.762v.747c-3.517-1.183-7.277-1.834-11.188-1.834-6.792 0-13.393 1.952-19.087 5.646-5.561 3.606-7.145 11.037-3.538 16.598 3.606 5.561 11.039 7.145 16.598 3.538 1.797-1.166 3.881-1.782 6.028-1.782 6.169 0 11.188 5.019 11.188 11.188v116.222c0 6.169-5.019 11.187-11.188 11.187-2.153 0-4.257-.619-6.084-1.789-5.579-3.575-13.002-1.95-16.578 3.63-3.575 5.58-1.95 13.002 3.63 16.578 5.699 3.651 12.279 5.581 19.031 5.581 3.911 0 7.67-.651 11.188-1.834v.776c0 15.844-12.89 28.734-28.734 28.734zm150.89-93.934c-3.417 1.236-6.09 3.953-7.269 7.389-1.18 3.437-.738 7.223 1.2 10.296 2.907 4.61 4.443 9.915 4.443 15.342 0 15.546-12.638 28.437-28.171 28.736-5.57.107-10.333 4.034-11.502 9.481-2.821 13.148-14.648 22.69-28.123 22.69-15.844 0-28.734-12.89-28.734-28.734v-.776c3.517 1.183 7.277 1.834 11.188 1.834 6.751 0 13.332-1.93 19.031-5.581 5.581-3.575 7.206-10.997 3.631-16.578-3.576-5.581-10.999-7.206-16.578-3.631-1.827 1.171-3.931 1.79-6.084 1.79-6.169 0-11.188-5.019-11.188-11.187v-116.222c0-6.169 5.019-11.188 11.188-11.188 2.146 0 4.23.616 6.028 1.782 5.561 3.606 12.992 2.022 16.598-3.539s2.021-12.991-3.539-16.597c-5.695-3.694-12.295-5.646-19.087-5.646-3.911 0-7.67.651-11.188 1.834v-.747c0-15.86 12.89-28.762 28.734-28.762 13.476 0 25.302 9.553 28.122 22.715 1.167 5.449 5.932 9.377 11.503 9.485 15.534.298 28.171 13.189 28.171 28.736 0 5.422-1.534 10.723-4.437 15.33-3.825 6.04-9.708 10.425-16.566 12.347-6.381 1.789-10.104 8.412-8.315 14.794 1.485 5.298 6.303 8.764 11.548 8.764 1.072 0 2.163-.145 3.246-.449 8.953-2.51 16.987-7.312 23.406-13.854 7.206 5.328 11.698 13.857 11.698 23.105 0 12.073-7.617 22.94-18.954 27.04zm-43.936.298c-2.121 4.156-6.332 6.548-10.699 6.548-1.835 0-3.698-.422-5.444-1.313-6.302-3.216-11.526-8.185-15.107-14.371-10.192-17.658-4.126-40.296 13.514-50.478 5.742-3.312 13.08-1.345 16.392 4.394 3.313 5.74 1.346 13.079-4.394 16.392-6.179 3.567-8.303 11.498-4.734 17.681 1.25 2.16 3.065 3.896 5.238 5.005 5.903 3.012 8.247 10.24 5.235 16.143zm152.801 48.17c8.402-24.315 12.666-49.753 12.666-75.763 0-98.853-62.75-186.901-156.144-219.098-6.268-2.16-13.096 1.169-15.256 7.434-2.16 6.266 1.168 13.096 7.434 15.256 83.718 28.861 139.966 107.791 139.966 196.408 0 22.56-3.575 44.632-10.619 65.771-17.649-2.708-35.99 5.396-45.463 21.781-12.276 21.301-4.953 48.639 16.333 60.947 6.879 3.97 14.512 5.998 22.246 5.998 3.877 0 7.781-.51 11.626-1.541 11.516-3.086 21.134-10.471 27.083-20.792 10.953-18.987 6.323-42.749-9.872-56.4zm-10.919 44.409-.003.006c-2.745 4.762-7.185 8.17-12.502 9.595-5.324 1.427-10.886.695-15.654-2.057-9.837-5.688-13.222-18.33-7.552-28.166 3.822-6.61 10.767-10.309 17.897-10.309 3.496 0 7.038.89 10.276 2.762 9.843 5.681 13.225 18.317 7.539 28.169zm-423.833-66.331c.752 0 1.516-.071 2.281-.219 6.508-1.253 10.768-7.544 9.516-14.052-2.49-12.935-3.752-26.249-3.752-39.571 0-98.468 70.678-184.063 166.112-203.498 6.496 16.625 22.677 28.438 41.575 28.438 24.607 0 44.627-20.02 44.627-44.627s-20.02-44.627-44.627-44.627c-21.939 0-40.219 15.918-43.927 36.809-50.633 9.728-96.837 36.302-130.955 75.529-36.631 42.117-56.804 96.09-56.804 151.976 0 14.841 1.408 29.681 4.185 44.107 1.105 5.742 6.132 9.734 11.771 9.734zm215.732-294.155c11.374 0 20.627 9.253 20.627 20.627s-9.253 20.627-20.627 20.627-20.627-9.253-20.627-20.627 9.253-20.627 20.627-20.627zm136.186 397.141c-37.732 32.797-86.097 50.859-136.186 50.859-59.916 0-115.961-25.362-155.442-69.943 11.124-13.936 13.261-33.842 3.829-50.207-12.32-21.306-39.663-28.625-60.948-16.32-21.301 12.294-28.626 39.634-16.332 60.946 5.949 10.322 15.568 17.706 27.083 20.792 3.845 1.031 7.747 1.541 11.625 1.541 5.162 0 10.275-.925 15.156-2.707 44.117 50.889 107.355 79.897 175.029 79.897 55.875 0 109.831-20.153 151.93-56.745 5.002-4.348 5.533-11.927 1.185-16.929-4.347-5.002-11.927-5.532-16.929-1.185zm-331.782-27.054c-5.317-1.425-9.758-4.833-12.502-9.595l-.003-.006c-5.686-9.852-2.304-22.489 7.546-28.174 3.236-1.871 6.774-2.76 10.27-2.76 7.127 0 14.073 3.697 17.889 10.297 5.678 9.851 2.293 22.493-7.535 28.176-4.777 2.757-10.34 3.489-15.664 2.062zm145.52-96.946c-1.746.891-3.609 1.313-5.444 1.313-4.367 0-8.578-2.391-10.699-6.548-3.012-5.903-.668-13.131 5.235-16.143 2.173-1.109 3.987-2.844 5.245-5.018 3.561-6.17 1.438-14.101-4.741-17.668-5.74-3.313-7.707-10.652-4.394-16.392s10.653-7.707 16.392-4.394c17.64 10.182 23.706 32.82 13.521 50.464-3.588 6.199-8.812 11.169-15.115 14.385z"
    />
  </svg>
)

export const ComplianceIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17.336"
    height="21.235"
    viewBox="0 0 17.336 21.235"
  >
    <g id="policy" transform="translate(-61.337 -14.4)">
      <path
        id="Path_12509"
        data-name="Path 12509"
        d="M72.173,33.782H61.937V15h10l3.756,3.756V26.8"
        transform="translate(0)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <path
        id="Path_12510"
        data-name="Path 12510"
        d="M302.5,15v3.756h3.756"
        transform="translate(-230.564)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <path
        id="Path_12511"
        data-name="Path 12511"
        d="M315.259,357.7l1.57,1.57,2.378-2.378"
        transform="translate(-242.793 -327.682)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <line
        id="Line_1357"
        data-name="Line 1357"
        x2="3.751"
        transform="translate(63.818 19.631)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <path
        id="Path_12512"
        data-name="Path 12512"
        d="M278.968,307.009s-3.629-1.21-3.633-3.633v-3.392l2.381-1.178h2.5l2.381,1.178v3.392C282.6,305.8,278.968,307.009,278.968,307.009Z"
        transform="translate(-204.528 -272.009)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <line
        id="Line_1358"
        data-name="Line 1358"
        x2="9.994"
        transform="translate(63.818 23.138)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <line
        id="Line_1359"
        data-name="Line 1359"
        x2="6.249"
        transform="translate(63.818 25.643)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
      <line
        id="Line_1360"
        data-name="Line 1360"
        x2="3.751"
        transform="translate(63.818 29.149)"
        fill="none"
        stroke={isSelected ? "#009ef7" : colors.starkWhite}
        strokeWidth="1.2"
      />
    </g>
  </svg>
)

export const Provider_Production_SummaryIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    //xmlns:xlink="http://www.w3.org/1999/xlink"
    width="21.21"
    height="21.21"
    viewBox="0 0 21.21 21.21"
  >
    <defs>
      <clipPath id="clip-path">
        <path
          id="path633"
          d="M0-682.665H21.21v21.21H0Z"
          transform="translate(0 682.665)"
        />
      </clipPath>
    </defs>
    <g id="g627" transform="translate(0 682.665)">
      <g id="g629" transform="translate(0 -682.665)">
        <g id="g631" clip-path="url(#clip-path)">
          <g id="g637" transform="translate(3.19 3.107)">
            <path
              id="path639"
              d="M-465.086-129.19v13.339H-480V-132.09a1.243,1.243,0,0,1,1.243-1.243h14.913"
              transform="translate(479.999 133.333)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g641" transform="translate(18.103 3.107)">
            <path
              id="path643"
              d="M-77.514-129.19H-80v-2.9a1.243,1.243,0,0,1,1.243-1.243,1.243,1.243,0,0,1,1.243,1.243Z"
              transform="translate(80 133.333)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g645" transform="translate(0.621 4.764)">
            <path
              id="path647"
              d="M-80.1-493.508h-2.568v-14.582a1.243,1.243,0,0,1,1.243-1.243H-80.1"
              transform="translate(82.666 509.332)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g649" transform="translate(13.132 0.621)">
            <path
              id="path651"
              d="M-77.514-155.029a1.243,1.243,0,0,1-1.243,1.243A1.243,1.243,0,0,1-80-155.029v-3.728A1.243,1.243,0,0,1-78.757-160a1.243,1.243,0,0,1,1.243,1.243v1.243"
              transform="translate(80 160)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g653" transform="translate(5.675 9.776)">
            <path
              id="path655"
              d="M-142.109,0h-1.657a2.9,2.9,0,0,0-2.9,2.9v.414h7.457V2.9A2.9,2.9,0,0,0-142.109,0Z"
              transform="translate(146.666)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g657" transform="translate(7.332 5.634)">
            <path
              id="path659"
              d="M-129.19-64.6a2.071,2.071,0,0,1-2.071,2.071,2.071,2.071,0,0,1-2.071-2.071,2.071,2.071,0,0,1,2.071-2.071A2.071,2.071,0,0,1-129.19-64.6Z"
              transform="translate(133.333 66.667)"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g661" transform="translate(5.675 15.576)">
            <path
              id="path663"
              d="M0,0H9.942"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
          <g id="g665" transform="translate(5.675 18.062)">
            <path
              id="path667"
              d="M0,0H9.942"
              fill="none"
              stroke={isSelected ? "#009ef7" : colors.starkWhite}
              stroke-linecap="round"
              stroke-linejoin="round"
              strokeWidth="1.2"
            />
          </g>
        </g>
      </g>
    </g>
  </svg>
)

export const HelpCenterIcon = ({ isSelected }: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21.209"
    height="17.889"
    viewBox="0 0 21.209 17.889"
  >
    <g id="folder" transform="translate(0 -0.5)">
      <path
        id="Path_12513"
        data-name="Path 12513"
        d="M19.936,2.751H12.642a.025.025,0,0,1-.023-.014l-.752-1.526A1.281,1.281,0,0,0,10.725.5H6.315A1.275,1.275,0,0,0,5.042,1.773V3.021H3.794A1.274,1.274,0,0,0,2.521,4.294V5.542H1.273A1.274,1.274,0,0,0,0,6.815v10.3a1.274,1.274,0,0,0,1.273,1.273H14.894a1.274,1.274,0,0,0,1.273-1.273V15.868h1.248A1.274,1.274,0,0,0,18.688,14.6V13.347h1.248a1.274,1.274,0,0,0,1.273-1.273V4.024A1.275,1.275,0,0,0,19.936,2.751ZM14.894,17.142H1.273a.025.025,0,0,1-.025-.025V6.815a.025.025,0,0,1,.025-.025h4.41a.025.025,0,0,1,.012,0h0l0,0a.027.027,0,0,1,.006.006l.753,1.526a1.265,1.265,0,0,0,1.142.71h7.294l.01,0a.025.025,0,0,1,.015.023c0,4.4,0,8.01,0,8.05A.025.025,0,0,1,14.894,17.142ZM17.44,14.6a.025.025,0,0,1-.025.025H16.167V9.066a1.275,1.275,0,0,0-1.273-1.273H7.6a.026.026,0,0,1-.023-.014L6.824,6.252a1.266,1.266,0,0,0-1.142-.71H3.769V4.294a.025.025,0,0,1,.025-.025H8.2a.025.025,0,0,1,.023.014L8.979,5.81a1.265,1.265,0,0,0,1.142.71h7.294a.025.025,0,0,1,.025.025Zm2.521-2.521a.025.025,0,0,1-.025.025H18.688V6.545a1.274,1.274,0,0,0-1.273-1.273H10.121a.026.026,0,0,1-.023-.014L9.345,3.732A1.264,1.264,0,0,0,8.2,3.021H6.29V1.773a.025.025,0,0,1,.025-.026h4.409a.025.025,0,0,1,.023.014L11.5,3.289A1.266,1.266,0,0,0,12.642,4h7.294a.026.026,0,0,1,.025.026v8.05Zm0,0"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
      />
      <path
        id="Path_12514"
        data-name="Path 12514"
        d="M253.167,336.766a.624.624,0,0,0-.624.624v.361a.624.624,0,0,0,1.248,0v-.361A.624.624,0,0,0,253.167,336.766Zm0,0"
        transform="translate(-242.082 -322.336)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
      />
      <path
        id="Path_12515"
        data-name="Path 12515"
        d="M297.05,336.766a.624.624,0,0,0-.624.624v.361a.624.624,0,0,0,1.248,0v-.361A.624.624,0,0,0,297.05,336.766Zm0,0"
        transform="translate(-284.147 -322.336)"
        fill={isSelected ? "#009ef7" : colors.starkWhite}
      />
    </g>
  </svg>
)
